import {
  type AdminExhibitionPurpose,
  EXHIBITION_PURPOSE_NAME_MAX_LENGTH,
} from '@repo/api-contracts';
import { type ReactElement, useRef, useState } from 'react';
import { useExhibitionPurposesQuery, useInvalidateExhibitions } from '@/entities/exhibition';
import {
  ExhibitionPurposeTabList,
  useCreateExhibitionPurpose,
  useDeleteExhibitionPurpose,
  useRenameExhibitionPurpose,
  useReorderExhibitionPurposes,
} from '@/features/exhibition-purposes';
import { Button, ContentCard, EmptyState, NameFormModal } from '@/shared/ui';
import { ExhibitionListSection } from './exhibition-list-section';

const DELETE_CONFIRM = '삭제시 예하에 있는 기획전 내용이 모두 삭제됩니다. 그래도 삭제하시겠습니까?';

type PurposeForm =
  | { readonly mode: 'create' }
  | { readonly mode: 'rename'; readonly purpose: AdminExhibitionPurpose };

/** 기획전 관리 — 위쪽 목적 탭(추가·이름 변경·삭제·순서)과 고른 목적의 기획전 목록. */
export const ExhibitionManager = (): ReactElement => {
  const purposesQuery = useExhibitionPurposesQuery();
  const { invalidatePurposes, invalidateAll } = useInvalidateExhibitions();
  const [selectedPurposeId, setSelectedPurposeId] = useState<string | null>(null);
  const [form, setForm] = useState<PurposeForm | null>(null);
  // 아래 목록의 기획전 요청(추가·수정·공개 전환·이동·삭제)이 진행 중인지 — 목적 탭도 함께 잠근다.
  const [isExhibitionBusy, setIsExhibitionBusy] = useState(false);
  // 삭제 요청과 성공 사이에 선택 탭 판정이 필요하다 — 리렌더를 기다리지 않게 ref 에 담는다.
  const deletingPurposeIdRef = useRef<string | null>(null);

  const createAction = useCreateExhibitionPurpose({
    onSuccess: (purpose) => {
      invalidatePurposes();
      setSelectedPurposeId(purpose.id);
      setForm(null);
    },
  });
  const renameAction = useRenameExhibitionPurpose({
    onSuccess: () => {
      invalidatePurposes();
      setForm(null);
    },
  });
  const reorderAction = useReorderExhibitionPurposes({ onSuccess: invalidatePurposes });
  const deleteAction = useDeleteExhibitionPurpose({
    onSuccess: () => {
      invalidateAll();

      // 삭제한 탭이 선택 중이었으면 첫 번째 탭으로 돌아간다.
      if (deletingPurposeIdRef.current === selectedPurposeId) {
        setSelectedPurposeId(null);
      }

      deletingPurposeIdRef.current = null;
    },
  });

  const purposes = purposesQuery.data ?? [];
  const selectedPurpose =
    purposes.find((purpose) => purpose.id === selectedPurposeId) ?? purposes[0] ?? null;
  const isBusy =
    createAction.isPending ||
    renameAction.isPending ||
    reorderAction.isPending ||
    deleteAction.isPending;
  // 아래 목록의 기획전 요청도 함께 본다 — 목적 삭제 중에 그 목록 버튼만 살아 있지 않게(§8-6).
  const isAnyBusy = isBusy || isExhibitionBusy;
  // 추가·이름 변경 오류는 그 모달 안에서만 보여 준다 — 페이지 배너와 겹쳐 두 번 나오고,
  // 모달을 닫아도 배너에 남는 것을 막는다. 모달이 없는 재배치·삭제만 배너에 올린다.
  const purposeError = reorderAction.error?.message ?? deleteAction.error?.message ?? null;

  // 재배치·삭제는 배너 한 줄(purposeError)을 함께 쓴다. 새 요청을 보내기 전에 두 요청의 지난 오류를
  // 모두 지운다 — 아니면 다른 요청이 성공해도 옛 오류가 그 요청이 다시 돌 때까지 배너에 남는다.
  const clearPurposeErrors = (): void => {
    reorderAction.reset();
    deleteAction.reset();
  };

  const handleReorder = (orderedIds: readonly string[]): void => {
    clearPurposeErrors();
    reorderAction.reorderPurposes(orderedIds);
  };

  const handleDelete = (purpose: AdminExhibitionPurpose): void => {
    if (window.confirm(DELETE_CONFIRM)) {
      clearPurposeErrors();
      deletingPurposeIdRef.current = purpose.id;
      deleteAction.deletePurpose(purpose.id);
    }
  };

  // 모달을 열고 닫을 때 지난 추가·이름 변경 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeForm = (next: PurposeForm | null): void => {
    createAction.reset();
    renameAction.reset();
    setForm(next);
  };

  const handleSubmitName = (name: string): void => {
    if (form?.mode === 'rename') {
      renameAction.renamePurpose(form.purpose.id, name);
      return;
    }

    createAction.createPurpose(name);
  };

  return (
    <ContentCard
      title="목적별 기획전"
      actions={
        <Button
          variant="outline"
          size="sm"
          disabled={isAnyBusy}
          onClick={() => changeForm({ mode: 'create' })}
        >
          + 목적 추가
        </Button>
      }
    >
      {purposeError !== null ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {purposeError}
        </p>
      ) : null}

      {purposesQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : purposesQuery.isError ? (
        <EmptyState isError>
          목적을 불러오지 못했습니다.{' '}
          <button type="button" className="underline" onClick={() => void purposesQuery.refetch()}>
            다시 시도
          </button>
        </EmptyState>
      ) : purposes.length === 0 ? (
        <EmptyState>목적을 먼저 추가하세요.</EmptyState>
      ) : (
        <div>
          <ExhibitionPurposeTabList
            purposes={purposes}
            selectedPurposeId={selectedPurpose?.id ?? null}
            isBusy={isAnyBusy}
            onSelect={setSelectedPurposeId}
            onRename={(purpose) => changeForm({ mode: 'rename', purpose })}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            탭을 끌어서 순서를 바꾸면 고객 기획전 페이지의 탭 순서도 함께 바뀝니다.
          </p>
        </div>
      )}

      {/* 목적마다 새로 마운트한다 — 앞 목적의 공개 전환·이동·삭제 오류가 다른 목적의 목록 위에 남지 않게.
          목록 요청 중에는 목적 탭이 잠겨 있어 요청 도중에 다시 마운트되지 않는다.
          key 에 접두어를 붙인다 — 형제인 이름 변경 모달의 key(목적 id)와 겹치지 않게. */}
      {selectedPurpose !== null ? (
        <ExhibitionListSection
          key={`exhibitions-${selectedPurpose.id}`}
          purpose={selectedPurpose}
          purposes={purposes}
          isPurposeBusy={isBusy}
          onBusyChange={setIsExhibitionBusy}
        />
      ) : null}

      {form !== null ? (
        <NameFormModal
          key={form.mode === 'rename' ? form.purpose.id : 'create'}
          title={form.mode === 'create' ? '목적 추가' : '목적 이름 변경'}
          label="목적 이름"
          placeholder="예: 웰컴키트"
          initialName={form.mode === 'rename' ? form.purpose.name : ''}
          submitLabel={form.mode === 'create' ? '추가하기' : '저장하기'}
          maxLength={EXHIBITION_PURPOSE_NAME_MAX_LENGTH}
          isPending={createAction.isPending || renameAction.isPending}
          errorMessage={
            (form.mode === 'create' ? createAction.error : renameAction.error)?.message ?? null
          }
          onSubmit={handleSubmitName}
          onClose={() => changeForm(null)}
        />
      ) : null}
    </ContentCard>
  );
};
